import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import "./LayersSidebar.css";

const GROUPS = [
  { id: "relief", label: "Relief & 3D" },
  { id: "data", label: "Données" },
];

const Chevron = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
    <path
      d="M4 2.5L7.5 6 4 9.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// supports { stops: [{ color, label }] } and the old { colors, min, max }
const toStops = (gradient) =>
  gradient.stops ??
  gradient.colors.map((color, i, arr) => ({
    color,
    label: i === 0 ? gradient.min : i === arr.length - 1 ? gradient.max : null,
  }));

const LayerGradient = ({ gradient }) => {
  const stops = toStops(gradient);
  const last = stops.length - 1;
  const pct = (i) => (last === 0 ? 0 : (i / last) * 100);
  // keep the first and last labels inside the bar
  const shift = (i) => (i === 0 ? "0" : i === last ? "-100%" : "-50%");

  const background = `linear-gradient(90deg, ${stops
    .map((s, i) => `${s.color} ${pct(i)}%`)
    .join(", ")})`;

  return (
    <div className="ls-gradient">
      <span className="ls-gradient__bar" style={{ background }} />
      <span className="ls-gradient__labels">
        {stops.map(
          (s, i) =>
            s.label && (
              <span
                key={i}
                className="ls-gradient__label"
                style={{
                  left: `${pct(i)}%`,
                  transform: `translateX(${shift(i)})`,
                }}
              >
                {s.label}
              </span>
            ),
        )}
      </span>
      {gradient.unit && (
        <span className="ls-gradient__unit">{gradient.unit}</span>
      )}
    </div>
  );
};

const LayersSidebar = ({ layers, open, onClose }) => {
  const [expanded, setExpanded] = useState({});
  const activeCount = layers.filter((l) => l.showItem).length;
  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // switching a layer on also opens its legend
  const handleSwitch = (layer, hasLegend) => (e) => {
    if (e.target.checked && hasLegend) {
      setExpanded((prev) => ({ ...prev, [layer.id]: true }));
    }
    layer.onChange?.(e);
  };

  const renderLayer = (layer) => {
    const isOpen = !!expanded[layer.id];
    const hasLegend = !!(layer.classes?.length || layer.gradient);

    return (
      <li
        key={layer.id}
        className={`ls-layer ${layer.showItem ? "is-on" : ""} ${
          layer.disabled ? "is-disabled" : ""
        }`}
      >
        <div className="ls-layer__row">
          <button
            type="button"
            className="ls-layer__main"
            onClick={() => hasLegend && toggleExpand(layer.id)}
            aria-expanded={hasLegend ? isOpen : undefined}
            disabled={!hasLegend}
          >
            <span className={`ls-layer__chevron ${isOpen ? "is-open" : ""}`}>
              {hasLegend && <Chevron />}
            </span>
            <span className="ls-layer__text">
              <span className="ls-layer__name">
                {layer.title}
                {layer.soon && <span className="ls-tag">Bientôt</span>}
              </span>
              {layer.description && (
                <span className="ls-layer__desc">{layer.description}</span>
              )}
            </span>
          </button>

          <label className="ls-switch">
            <input
              type="checkbox"
              role="switch"
              checked={layer.showItem}
              disabled={layer.disabled}
              onChange={handleSwitch(layer, hasLegend)}
              aria-label={`Afficher ${layer.title}`}
            />
            <span className="ls-switch__track" />
          </label>
        </div>

        {hasLegend && (
          <div className={`ls-legend ${isOpen ? "is-open" : ""}`}>
            <div className="ls-legend__inner">
              {layer.classes && (
                <ul className="ls-legend__list">
                  {layer.classes.map((c) => (
                    <li key={c.label}>
                      <span
                        className="ls-legend__dot"
                        style={{ background: c.color }}
                      />
                      <span className="ls-legend__label">{c.label}</span>
                    </li>
                  ))}
                </ul>
              )}
              {layer.gradient && <LayerGradient gradient={layer.gradient} />}
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <aside
      className={`layers-panel ${open ? "is-open" : ""}`}
      aria-hidden={!open}
      {...(!open ? { inert: "" } : {})}
    >
      <div className="layers-panel__header">
        <h2 className="layers-panel__title">Couches</h2>
        <div className="layers-panel__header-actions">
          <span
            className={`layers-panel__count ${activeCount ? "is-active" : ""}`}
          >
            {activeCount}/{layers.length} actives
          </span>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Fermer les couches"
            sx={{ color: "var(--color-burgundy)", flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {GROUPS.map((g) => {
        const items = layers.filter((l) => (l.group || "data") === g.id);
        if (!items.length) return null;
        return (
          <section key={g.id} className="ls-group">
            <h3 className="ls-group__label">{g.label}</h3>
            <ul className="ls-list">{items.map(renderLayer)}</ul>
          </section>
        );
      })}
    </aside>
  );
};

export default LayersSidebar;
